import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { put } from "../api";
import { environmentConfig } from "../environment";
import { APP_V2_APP_EVENTS } from "../lib";
import { invalidateGetWorkerQuery } from "./api/useGetWorker";
import { useDefinedWorker } from "./useDefinedWorker";

const UPDATE_WORKER_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/put`;

const updateWorkerRequestSchema = z.object({
  agentId: z.string().optional(),
  fullSocialSecurityNumber: z.string().optional(),
  dob: z.string().optional(),
});

type UpdateWorkerRequest = z.infer<typeof updateWorkerRequestSchema>;

const updateWorkerResponseSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  fullSocialSecurityNumber: z.string().optional(),
  socialSecurityNumber: z.string().optional(),
  dob: z.string().optional(),
});

type UpdateWorkerResponse = z.infer<typeof updateWorkerResponseSchema>;

export function useUpdateWorker(
  options: UseMutationOptions<UpdateWorkerResponse, AxiosError, UpdateWorkerRequest> = {}
) {
  const worker = useDefinedWorker();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_WORKER_URL],
    mutationFn: async (updateWorkerPayload: UpdateWorkerRequest) => {
      const response = await put({
        url: UPDATE_WORKER_URL,
        data: {
          ...updateWorkerPayload,
          agentId: worker.userId,
        },
        responseSchema: updateWorkerResponseSchema,
        requestSchema: updateWorkerRequestSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_AGENT_DATA_FAILURE,
    },
    onSuccess: async () => {
      void invalidateGetWorkerQuery(queryClient);
    },
    ...options,
  });
}
